import { useLayoutEffect, useRef } from 'react';
import { cn } from '~/util';

type FitTextToWidthProps = {
    children: string;
    minPx?: number;
    className?: string;
    groupPx?: number | null;
    measureKey?: unknown;
    onMeasure?: (proposedPx: number, basePx: number) => void;
};

export function FitTextToWidth({
    children,
    minPx = 10,
    className,
    groupPx,
    measureKey,
    onMeasure,
}: FitTextToWidthProps) {
    const containerRef = useRef<HTMLSpanElement>(null);

    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const fit = () => {
            element.style.fontSize = '';
            element.style.whiteSpace = 'nowrap';

            const computed = getComputedStyle(element);
            const basePx = parseFloat(computed.fontSize || '16');

            const parent = element.parentElement as HTMLElement | null;
            const available = parent ? parent.clientWidth : element.clientWidth;
            const natural = element.scrollWidth;

            const proposedPx =
                natural <= available || natural === 0 || available === 0
                    ? basePx
                    : basePx * (available / natural);

            const nextPx = Math.max(minPx, proposedPx);

            onMeasure?.(nextPx, basePx);

            if (groupPx != null) {
                element.style.fontSize = `${Math.max(minPx, groupPx)}px`;
            } else if (nextPx >= basePx) {
                element.style.fontSize = '';
            } else {
                element.style.fontSize = `${nextPx}px`;
            }
        };

        fit();

        const resizeObserver = new ResizeObserver(fit);

        resizeObserver.observe(element);

        if (element.parentElement) {
            resizeObserver.observe(element.parentElement);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [children, minPx, groupPx, measureKey, onMeasure]);

    return (
        <span
            ref={containerRef}
            className={cn(
                className,
                'inline-block whitespace-nowrap pointer-events-none color-[inherit] font-[inherit] leading-[inherit] tracking-[inherit] text-inherit align-middle',
            )}
            aria-hidden={false}
        >
            {children}
        </span>
    );
}
